<template>
  <v-row :id="`${cachedItem.id}-update-panel`">
    <div class="card-logo">
      <img
        :alt="cardImageAlt"
        :src="cachedItem.image"
        class="img-responsive image"
      >
    </div>
    <v-col
      cols="8"
      sm="5"
      class="text-left"
    >
      <div class="name">
        {{ cachedItem.name }}
      </div>
      <div class="edit-value">
        <span
          v-if="isVariableItem"
          class="edit-variable-container"
        >
          <v-text-field
            id="input-reward-amount"
            v-model="itemAmount"
            v-validate="variableItemValidationRules"
            aria-label="Edit Reward Amount"
            type="number"
            name="input-reward-amount"
            solo
            flat
            hide-details
          >
            <template #prepend>
              <span class="currency">
                {{ cachedItem.currencyCode }}
              </span>
            </template>
          </v-text-field>
        </span>
        <span
          v-else
          class="edit-fixed-container"
        >
          <amount-fixed-input
            id="reward-amount-fixed"
            v-model="itemUtid"
            :item-options="getFilteredFixedValueItems"
            :selected-item="getBrand"
            :cart-item="cachedItem"
            name="reward-amount-fixed"
          />
        </span>
      </div>
      <div
        v-if="hasErrors(errors)"
        class="error-block"
        tabindex="0"
      >
        <div v-if="isVariableItem">
          <validation-tip
            id="input-reward-amount-error-required"
            aria-invalid="false"
            class="error-message"
            :error-bag="errors"
            rule="required"
            control="input-reward-amount"
            i18n-key="reward-variable-valid-amount"
            aria-live="assertive"
          />
          <validation-tip
            id="input-reward-amount-error-min"
            :error-bag="errors"
            rule="min_value"
            control="input-reward-amount"
            i18n-key="reward-variable-valid-amount-min"
            tabindex="0"
            aria-live="assertive"
          />
          <validation-tip
            id="input-reward-amount-error-max"
            :error-bag="errors"
            rule="max_value"
            control="input-reward-amount"
            i18n-key="reward-variable-valid-amount-max"
            tabindex="0"
            aria-live="assertive"
          />
          <validation-tip
            id="input-reward-amount-error-valid-amount"
            :error-bag="errors"
            rule="remainingBalance"
            control="input-reward-amount"
            i18n-key="reward-variable-valid-amount-balance"
            tabindex="0"
            aria-live="assertive"
          />
          <validation-tip
            id="input-reward-amount-error-valid-amount-with-fee"
            :error-bag="errors"
            rule="feeCheck"
            control="input-reward-amount"
            i18n-key="reward-variable-valid-amount-balance-with-fee"
            tabindex="0"
            aria-live="assertive"
          />
        </div>
      </div>
    </v-col>
    <v-col
      cols="3"
      sm="4"
      class="controls text-right pl-0 pr-1 px-sm-2"
    >
      <input
        :value="cachedItem.id"
        type="hidden"
        class="cartItemId"
      >
      <tc-v-btn
        class="edit"
        color="secondary"
        large
        aria-label="Update amount."
        @click="confirmUpdate"
      >
        {{ $t('checkout-update') }}
      </tc-v-btn>
      <br class="d-block d-sm-none">
      <tc-v-btn
        class="remove"
        tabindex="0"
        color="secondary"
        large
        outlined
        @click="removeFromCart(cachedItem)"
        @keyup.enter.prevent="removeFromCart(cachedItem)"
      >
        <span class="d-none d-sm-inline-block">
          {{ $t('checkout-remove') }}
        </span>
        <v-icon class="d-inline-block d-sm-none">
          mdi-close
        </v-icon>
      </tc-v-btn>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  UPDATE_ITEM_VALUE,
  REMOVE_CART_ITEM,
  UPDATE_REWARD_LINK_BALANCE
} from '../../store/action-types'
import { GET_REWARD_LINK_BALANCE } from '../../store/get-types'
import ValidationTip from '../../components/shared/forms/ValidationTip'
import { ItemFeeMixin } from '../../components/shared/mixins/item-fee-mixin'
import AmountFixedInput from '../reward/details/AmountFixedInput'
import CartItem from '../../store/classes/cart-item'
import { get } from 'lodash'
import { FulfillmentType, ItemRewardValueType } from '@/cowboy/EnumTypes'

export default {
  name: 'ItemUpdatePanel',
  components: {
    AmountFixedInput,
    ValidationTip
  },
  mixins: [ItemFeeMixin],
  props: {
    cartItem: {
      type: [CartItem, Object],
      required: true
    }
  },
  data () {
    return {
      itemAmount: 0,
      itemUtid: null,
      cachedItem: {}
    }
  },
  computed: {
    ...mapGetters({
      availableBalance: GET_REWARD_LINK_BALANCE
    }),
    getBrand () {
      return this.cachedItem?.brand || {}
    },
    getBrandItems () {
      return this.cachedItem?.brand?.items
        ?.slice()
        ?.sort(
          (item, otherItem) =>
            parseFloat(item.faceValue) - parseFloat(otherItem.faceValue)
        ) || []
    },
    getFilteredFixedValueItems () {
      const fixedValueItems = this.getBrandItems.filter(
        item => item.valueType === ItemRewardValueType.FIXED_VALUE
      )
      if (this.itemIsPhysical) {
        return fixedValueItems.filter(
          item => item.fulfillmentType === FulfillmentType.PHYSICAL
        )
      } else if (this.itemIsDigital) {
        return fixedValueItems.filter(
          item => item.fulfillmentType === FulfillmentType.DIGITAL
        )
      } else {
        return fixedValueItems
      }
    },
    isVariableItem () {
      return this.cachedItem.valueType === ItemRewardValueType.VARIABLE_VALUE
    },
    balanceWithPreviouslySelectedAmount () {
      return (
        this.availableBalance +
        this.getAmountWithAppliedFeeIfItExists(
          this.cachedItem.selectedAmount,
          this.cachedItem.fee
        )
      )
    },
    variableItemValidationRules () {
      return {
        required: true,
        regex: /^\d*(\.\d{1,2})?$/,
        min_value: +this.cachedItem.brand?.items[0]?.minValue,
        max_value: +this.cachedItem.brand?.items[0]?.maxValue,
        remainingBalance: [this.balanceWithPreviouslySelectedAmount],
        feeCheck: [this.balanceWithPreviouslySelectedAmount, this.cachedItem]
      }
    },
    itemIsPhysical () {
      return this.cachedItem.fulfillmentType === FulfillmentType.PHYSICAL
    },
    itemIsDigital () {
      return this.cachedItem.fulfillmentType === FulfillmentType.DIGITAL
    },
    cartItemBalanceWithFee () {
      return this.getAmountWithAppliedFeeIfItExists(
        this.cachedItem.selectedAmount,
        this.cachedItem.fee
      )
    },
    cardImageAlt () {
      const { brand } = this.cachedItem || {}
      const { displayName = '' } = brand || {}
      return this.$t('checkout-item-control-card-image', { displayName }).trim()
    }
  },
  mounted () {
    this.cachedItem = JSON.parse(JSON.stringify(this.cartItem))
    this.itemAmount = `${this.cachedItem.selectedAmount}`
    this.itemUtid = this.cachedItem.utid
  },
  methods: {
    ...mapActions({
      updateItemValue: UPDATE_ITEM_VALUE,
      removeFromCart: REMOVE_CART_ITEM,
      updateBalance: UPDATE_REWARD_LINK_BALANCE
    }),
    async confirmUpdate () {
      const isValid = await this.$validator.validateAll()
      if (!isValid) {
        return
      }
      if (!this.isVariableItem) {
        this.updateFixedValue()
      } else {
        this.updateVariableValue()
      }
      this.$emit('finish-checkout-edit')
    },
    updateVariableValue () {
      const itemFee = this.getFormattedItemFee(
        this.itemAmount,
        this.cachedItem.fee
      )
      const newAmountWithFeeApplied = this.getAmountWithAppliedFeeIfItExists(
        this.itemAmount,
        itemFee
      )
      this.updateBalance(this.cartItemBalanceWithFee - newAmountWithFeeApplied)
      this.updateItemValue({
        cartItemId: this.cachedItem.id,
        newAmount: parseFloat(this.itemAmount), // make sure this is a number
        newItemFee: itemFee
      })
    },
    updateFixedValue () {
      const fixedValueItem = this.getFilteredFixedValueItems.find(
        item => item.utid === this.itemUtid
      )
      const { adjustment, faceValue } = fixedValueItem
      const itemFee = this.getFormattedItemFee(faceValue, adjustment)
      const newAmountWithFeeApplied = this.getAmountWithAppliedFeeIfItExists(
        faceValue,
        itemFee
      )
      this.updateBalance(this.cartItemBalanceWithFee - newAmountWithFeeApplied)
      this.updateItemValue({
        cartItemId: this.cachedItem.id,
        newAmount: parseFloat(faceValue), // make sure this is a number
        newItemFee: itemFee,
        newUtid: this.itemUtid
      })
    },
    hasErrors (errors) {
      return get(errors, 'items.length') > 0
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/css/variables';
@import '../../scss/variables';

.shopping-cart {
  .edit-value {
    padding-top: 0;
    line-height: 20px;
  }
}

.card-logo {
  width: 25%;
  padding: 0 15px;
  @media #{map-get($display-breakpoints, 'xs-only')} {
    display: none;
  }
}

::v-deep .edit-variable-container {
  .v-input {
    border: solid 2px var(--v-primary-base);
    .v-input__prepend-outer {
      background-color: var(--v-primary-base);
      height: 48px;
      min-width: 80px;
      margin: 0;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      font-size: 20px;
      display: flex;
      color: #fff;
    }
  }
}

.chosen-container {
  border: 1px solid $tangoBlueGrayDk;
  .chosen-single {
    border: 0;
    height: 52px;
    line-height: 50px;
    border-radius: 0;
    background: #fff;

    span {
      font-size: 20px;
      color: $tangoTextPrimary;
    }
  }

  div,
  b {
    display: none;
  }

  .chosen-drop {
    width: calc(100% + 2px);
    border-radius: 0;
    border: 1px solid $tangoBlueGrayDk;
    top: 0;
  }

  .chosen-results,
  li {
    font-size: $font-size-lg;
    color: $tangoTextPrimary;
    padding-top: 8px;
    padding-right: 25px;
  }
}

.chosen-container-active {
  &.chosen-with-drop {
    border-bottom: 0;
  }

  .chosen-drop {
    left: -1px;
  }

  .chosen-single {
    background-image: none;
    border: 0;

    div,
    b {
      background-position: -16px 2px;
    }
  }
}

@media screen and (min-width: $breakpoint-md) {
  .edit-value {
    .currency {
      z-index: 1;
      padding-top: 0;
      .chosen-container,
      .chosen-single {
        border: none;
        padding-left: 10px;
      }

      .chosen-drop {
        width: calc(100% + 2px);
      }

      .chosen-results,
      li {
        font-size: $font-size-md;
        padding-top: 8px;
        padding-right: 25px;
        color: $tangoTextPrimary;
      }
    }
  }
}
</style>
